import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';

const DISMISS_KEY = 'bge_pwa_dismissed_at';
const DISMISS_DAYS = 14;

const InstallPWAPrompt = () => {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    const recent = dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400000;

    const onPrompt = (e) => {
      e.preventDefault();
      if (recent) return;
      setDeferred(e);
      setVisible(true);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch {
      // ignore
    }
    setDeferred(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-50"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4 flex gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-text-primary text-sm">{t('pwa.install_title')}</div>
          <div className="text-xs text-text-secondary mt-0.5">{t('pwa.install_body')}</div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="px-4 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium"
              data-testid="pwa-install-btn"
            >
              {t('pwa.install_btn')}
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-1.5 rounded-full text-text-secondary text-xs hover:bg-slate-50"
              data-testid="pwa-dismiss-btn"
            >
              {t('pwa.dismiss')}
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-text-secondary hover:text-text-primary"
          aria-label="Dismiss"
          data-testid="pwa-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPWAPrompt;
